import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { expensesApi, projectsApi } from '../lib/api';
import { getErrorMessage } from '../lib/utils';
import { toast } from 'sonner';

const EMPTY_VALUES = {
  category: '',
  amount: '',
  date: '',
  status: 'pending',
  project_id: '',
  description: '',
  employee_name: '',
  employee_position: '',
};

export default function ExpenseDialog({ expense, isOpen, onClose }) {
  const queryClient = useQueryClient();
  const isEditing = !!expense;

  const { data: categoriesData } = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => expensesApi.getCategories().then((res) => res.data),
    enabled: isOpen,
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects-for-select'],
    queryFn: () => projectsApi.getAll({ limit: 100 }).then((res) => res.data),
    enabled: isOpen,
  });

  const { register, handleSubmit, control, reset, formState: { errors } } = useForm({
    defaultValues: EMPTY_VALUES,
  });

  React.useEffect(() => {
    if (isOpen) {
      if (expense) {
        const sourceDate = expense.actual_date || expense.planned_date;
        reset({
          category: expense.category || '',
          amount: expense.amount?.toString() || '',
          date: sourceDate ? new Date(sourceDate).toISOString().split('T')[0] : '',
          status: expense.status || 'pending',
          project_id: expense.project_id || '',
          description: expense.description || '',
          employee_name: expense.employee_name || '',
          employee_position: expense.employee_position || '',
        });
      } else {
        reset(EMPTY_VALUES);
      }
    }
  }, [isOpen, expense, reset]);

  const mutation = useMutation({
    mutationFn: (data) => (isEditing ? expensesApi.update(expense.id, data) : expensesApi.create(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expense-summary'] });
      queryClient.invalidateQueries({ queryKey: ['expense-categories'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      toast.success(isEditing ? 'Расход обновлён' : 'Расход добавлен');
      onClose();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Ошибка при сохранении расхода'));
    },
  });

  const onSubmit = (data) => {
    const dateValue = data.date || null;
    mutation.mutate({
      type: 'expense',
      category: data.category || null,
      amount: parseFloat(data.amount) || 0,
      status: data.status || 'pending',
      planned_date: dateValue,
      actual_date: data.status === 'paid' ? dateValue : null,
      project_id: data.project_id || null,
      description: data.description || null,
      employee_name: data.employee_name || null,
      employee_position: data.employee_position || null,
    });
  };

  const statusOptions = [
    { value: 'pending', label: 'Ожидает оплаты' },
    { value: 'paid', label: 'Оплачено' },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">
              {isEditing ? 'Редактировать расход' : 'Добавить расход'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500" data-testid="close-expense-dialog">
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Категория</label>
                <input
                  {...register('category')}
                  list="expense-category-suggestions"
                  placeholder="Например, Аренда"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="expense-category-input"
                />
                <datalist id="expense-category-suggestions">
                  {categoriesData?.categories?.map((cat) => (
                    <option key={cat} value={cat} />
                  ))}
                </datalist>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Сумма (руб.) *</label>
                <input
                  {...register('amount', { required: 'Обязательное поле' })}
                  type="number"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="expense-amount-input"
                />
                {errors.amount && <p className="text-red-500 text-sm mt-1">{errors.amount.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Дата *</label>
                <input
                  {...register('date', { required: 'Обязательное поле' })}
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="expense-date-input"
                />
                {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Статус</label>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <select
                      {...field}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="expense-status-select"
                    >
                      {statusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Проект</label>
                <Controller
                  name="project_id"
                  control={control}
                  render={({ field }) => (
                    <select
                      {...field}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="expense-project-select"
                    >
                      <option value="">Не привязан к проекту</option>
                      {projectsData?.projects?.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  )}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Сотрудник (ФИО)</label>
                <input
                  {...register('employee_name')}
                  placeholder="Например, Иванова Мария"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="expense-employee-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Должность</label>
                <input
                  {...register('employee_position')}
                  placeholder="Например, Менеджер проектов"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="expense-employee-position-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Описание</label>
              <textarea
                {...register('description')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="На что потрачено..."
                data-testid="expense-description-textarea"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                data-testid="cancel-expense-btn"
              >
                Отменить
              </button>
              <button
                type="submit"
                disabled={mutation.isPending}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                data-testid="save-expense-btn"
              >
                {mutation.isPending ? 'Сохранение...' : isEditing ? 'Сохранить' : 'Создать'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
